import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../services/api';
import { getRosterPublicationStatus, getShifts, RosterPublicationStatus, Shift } from '../../services/rosterService';
import { useAuth } from '../../contexts/AuthContext';
import RosterAvailabilityNotice from '../roster/RosterAvailabilityNotice';
import { getStaffShiftLabel } from '../../utils/rosterStyles';

const dateKey = (date: Date) => date.toISOString().split('T')[0];

const TimesheetSubmission = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [todayShifts, setTodayShifts] = useState<Shift[]>([]);
    const [selectedShiftId, setSelectedShiftId] = useState(searchParams.get('shiftId') || '');
    const [formData, setFormData] = useState({
        shiftDate: dateKey(new Date()),
        startTime: '',
        endTime: '',
        breakDuration: 0,
        overtimeReason: '',
        notes: '',
    });
    const [totalHours, setTotalHours] = useState('0.00');
    const [error, setError] = useState('');
    const [publicationStatus, setPublicationStatus] = useState<RosterPublicationStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        const loadTodayShifts = async () => {
            if (!user?.id) return;
            const start = new Date();
            start.setHours(0, 0, 0, 0);
            const end = new Date();
            end.setHours(23, 59, 59, 999);
            try {
                const [shifts, status] = await Promise.all([
                    getShifts(start, end, user.id),
                    getRosterPublicationStatus(start.getFullYear(), start.getMonth() + 1),
                ]);
                setTodayShifts(shifts);
                setPublicationStatus(status);
                const requested = searchParams.get('shiftId');
                const selected = shifts.find((shift: Shift) => shift.id === requested) || shifts[0];
                if (selected) {
                    setSelectedShiftId(selected.id);
                    setFormData(prev => ({
                        ...prev,
                        shiftDate: dateKey(new Date(selected.startTime)),
                        startTime: '',
                        endTime: '',
                        breakDuration: 0,
                    }));
                } else {
                    setSelectedShiftId('');
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load today’s rostered shift');
            } finally {
                setInitialLoading(false);
            }
        };
        loadTodayShifts();
    }, [user?.id, searchParams]);

    useEffect(() => {
        calculateTotalHours();
    }, [formData.startTime, formData.endTime, formData.breakDuration]);

    const calculateTotalHours = () => {
        const start = new Date(`2000-01-01T${formData.startTime}`);
        const end = new Date(`2000-01-01T${formData.endTime}`);
        if (!formData.startTime || !formData.endTime || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            setTotalHours('0.00');
            return;
        }
        let diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        if (diff < 0) diff += 24;
        const total = Math.max(0, diff - formData.breakDuration / 60).toFixed(2);
        setTotalHours(total);
    };

    const handleShiftChange = (shiftId: string) => {
        setSelectedShiftId(shiftId);
        const shift = todayShifts.find(item => item.id === shiftId);
        if (!shift) return;
        setFormData(prev => ({
            ...prev,
            shiftDate: dateKey(new Date(shift.startTime)),
            startTime: '',
            endTime: '',
            breakDuration: 0,
        }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        if (!selectedShiftId) {
            setError('You can only submit hours for a rostered shift today.');
            return;
        }
        if (!formData.startTime || !formData.endTime) {
            setError('Enter your actual start and finish time.');
            return;
        }
        setLoading(true);

        try {
            const shiftDate = new Date(formData.shiftDate);
            const startDateTime = new Date(`${formData.shiftDate}T${formData.startTime}`);
            const endDateTime = new Date(`${formData.shiftDate}T${formData.endTime}`);
            if (endDateTime < startDateTime) endDateTime.setDate(endDateTime.getDate() + 1);

            await apiClient.post('/timesheets', {
                ...formData,
                shiftId: selectedShiftId,
                shiftDate: shiftDate.toISOString(),
                startTime: startDateTime.toISOString(),
                endTime: endDateTime.toISOString(),
                totalHours: parseFloat(totalHours),
            });

            navigate('/timesheets/my');
        } catch (err: any) {
            setError(err.response?.data?.error?.message || 'Failed to submit timesheet');
        } finally {
            setLoading(false);
        }
    };

    const disabled = loading || initialLoading || todayShifts.length === 0 || !selectedShiftId;

    return (
        <div className="max-w-xl mx-auto">
            <div className="mb-5">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Submit Timesheet</h1>
                <p className="text-slate-500 mt-1">Hours can be submitted only for today’s rostered shift.</p>
            </div>

            {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>}

            {!initialLoading && publicationStatus === 'UNPUBLISHED' && (
                <div className="mb-4">
                    <RosterAvailabilityNotice status="UNPUBLISHED" context="timesheet" />
                </div>
            )}

            {!initialLoading && publicationStatus === 'NOT_CREATED' && (
                <div className="mb-4">
                    <RosterAvailabilityNotice status="NOT_CREATED" context="timesheet" />
                </div>
            )}

            {!initialLoading && publicationStatus === 'PUBLISHED' && todayShifts.length === 0 && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg">
                    You are not rostered today, so timesheet submission is disabled.
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-4 sm:p-6 space-y-5">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Today’s rostered shift</label>
                        <select
                            value={selectedShiftId}
                            onChange={(e) => handleShiftChange(e.target.value)}
                            disabled={todayShifts.length === 0}
                            className="w-full px-3 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none disabled:bg-slate-100"
                        >
                            <option value="">No rostered shift today</option>
                            {todayShifts.map(shift => (
                                <option key={shift.id} value={shift.id}>
                                    {getStaffShiftLabel(shift)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Shift Date</label>
                        <input value={formData.shiftDate} disabled className="w-full px-3 py-3 rounded-lg border border-slate-200 bg-slate-100 text-slate-600" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Start Time</label>
                            <input type="time" value={formData.startTime} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} required className="w-full px-3 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">End Time</label>
                            <input type="time" value={formData.endTime} onChange={(e) => setFormData({ ...formData, endTime: e.target.value })} required className="w-full px-3 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Break Duration (minutes)</label>
                        <input type="number" min="0" value={formData.breakDuration} onChange={(e) => setFormData({ ...formData, breakDuration: parseInt(e.target.value) || 0 })} required className="w-full px-3 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none" />
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-between border border-blue-100">
                        <div>
                            <p className="text-xs font-semibold text-blue-600 uppercase">Total Hours</p>
                            <p className="text-xs text-blue-500 mt-1">Actual hours can differ from roster.</p>
                        </div>
                        <p className="text-3xl font-bold text-blue-700">{totalHours}</p>
                    </div>

                    <textarea value={formData.overtimeReason} onChange={(e) => setFormData({ ...formData, overtimeReason: e.target.value })} className="w-full px-3 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none min-h-[80px] text-sm" placeholder="Overtime reason (optional)" />
                    <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full px-3 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none min-h-[80px] text-sm" placeholder="Notes (optional)" />
                </div>

                <div className="bg-slate-50 px-4 sm:px-6 py-4 border-t border-slate-100 flex gap-3">
                    <button type="button" onClick={() => navigate(-1)} className="flex-1 px-4 py-3 rounded-lg border border-slate-200 text-slate-600 font-semibold hover:bg-white">Cancel</button>
                    <button type="submit" disabled={disabled} className="flex-1 px-4 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {loading ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TimesheetSubmission;
